import { ReactElement, ReactNode, useEffect } from 'react'
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material'
import { GoogleTagManager } from '@next/third-parties/google'
import { NextPage } from 'next'
import { type AppContext, type AppProps, default as NextApp } from 'next/app'
import { League_Spartan, Poppins } from 'next/font/google'
import localFont from 'next/font/local'
import { useRouter } from 'next/router'

import SVGSprites from '@/components/base/svg-sprites'
import Chatbot from '@/components/base/third-parties/chatbot'
import DialogsProvider from '@/contexts/dialogs'
// Initialize i18n
import i18n from '@/contexts/i18n'
import { NotificationProvider } from '@/contexts/notification'
import { StoreProps, StoreProvider } from '@/contexts/store'
import { SWRProvider } from '@/contexts/swr'
import muiTheme from '@/styles/theme'
import { getPromiseFromFetch } from '@/utils/request'
import { initStatsToken } from '@/utils/stats-token'
import { API, GTM_ID } from '@/constants'

import '@/base/gsap'

import { BRAND_ID } from '@/config/brand'

// ─── SAIS Fonts ────────────────────────────────────────────────────────────────
const poppins = Poppins({
  weight: '500',
  variable: '--heading-font',
  subsets: ['latin'],
  display: 'swap'
})

// ─── HAPPA Fonts ───────────────────────────────────────────────────────────────
const leagueSpartan = League_Spartan({
  weight: ['400', '500', '600', '700'],
  variable: '--heading-font',
  subsets: ['latin'],
  display: 'swap'
})

const poppinsButton = Poppins({
  weight: ['400', '500', '600'],
  variable: '--button-font',
  subsets: ['latin'],
  display: 'swap'
})

// ─── Shared Fonts ──────────────────────────────────────────────────────────────
const baseFont = localFont({
  src: [
    { path: '../public/fonts/HelveticaNeue-Regular.woff2', weight: '400', style: 'normal' },
    { path: '../public/fonts/HelveticaNeue-Medium.woff2', weight: '500', style: 'normal' },
    { path: '../public/fonts/HelveticaNeue-Bold.woff2', weight: '700', style: 'normal' }
  ],
  variable: '--base-font',
  display: 'swap'
})

/** Font class names based on brand */
const brandFontClasses =
  BRAND_ID === 'happa'
    ? [leagueSpartan.variable, poppinsButton.variable, baseFont.variable]
    : [poppins.variable, baseFont.variable]

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export type AppPropsWithLayout = AppProps &
  StoreProps & {
    Component: NextPageWithLayout
  }

export default function App({ Component, pageProps, menu, strings }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page)
  const router = useRouter()
  const { locale } = router

  useEffect(() => {
    if (document.body) document.body.classList.add(...brandFontClasses)
  }, [])

  // Sync i18n language with Next.js locale
  useEffect(() => {
    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [locale])

  // Initialize stats token on first site access
  useEffect(() => {
    initStatsToken()
  }, [])

  // Track page changes in GTM for client-side navigation.
  useEffect(() => {
    if (!GTM_ID) return

    const handleRouteChange = (url: string) => {
      ;(window as Window & { dataLayer?: Array<Record<string, unknown>> }).dataLayer?.push({
        event: 'pageview',
        page: url
      })
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return (
    <MuiThemeProvider theme={muiTheme}>
      {GTM_ID ? <GoogleTagManager gtmId={GTM_ID} /> : null}
      <CssBaseline />
      <NotificationProvider>
        <DialogsProvider>
          <SWRProvider>
            <StoreProvider menu={menu} strings={strings}>
              {getLayout(<Component {...pageProps} />, pageProps)}
              <SVGSprites />
              <Chatbot />
            </StoreProvider>
          </SWRProvider>
        </DialogsProvider>
      </NotificationProvider>
    </MuiThemeProvider>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)
  const lang = appContext.router.locale

  const [{ data: menuData }, { data: optionsData }] = await Promise.all([
    getPromiseFromFetch({
      method: 'get',
      url: `${API.MENU_LOCATIONS}?lang=${lang}`
    }),
    getPromiseFromFetch({
      method: 'get',
      url: `${API.STRINGS}?lang=${lang}`
    })
  ])

  const sharedData = {
    strings: optionsData,
    menu: Object.keys(menuData).length > 0 ? menuData : {}
  }

  return {
    ...appProps,
    ...sharedData
  }
}
